import { useState, useEffect } from 'react';
import { Button, Spinner, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { authHelpers } from 'lib/supabase';
import Flex from 'components/common/Flex';
import LoadingButton from 'components/common/LoadingButton';

export default function TwoFactorAuth() {
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [factorId, setFactorId] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');

  // Disable confirmation modal state
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  // Check if MFA is already enabled
  useEffect(() => {
    checkMfaStatus();
  }, []);

  const checkMfaStatus = async () => {
    try {
      setLoading(true);

      // Clean up any unverified factors first
      await authHelpers.cleanupUnverifiedFactors();

      const { data, error } = await authHelpers.getMfaFactors();

      if (error) {
        console.error('Error checking MFA status:', error);
        return;
      }

      // Check for verified TOTP factors
      const verifiedFactor = data?.totp?.find(f => f.status === 'verified');
      if (verifiedFactor) {
        setMfaEnabled(true);
        setFactorId(verifiedFactor.id);
      }
    } catch (error) {
      console.error('MFA check error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollMfa = async () => {
    try {
      setEnrolling(true);
      const { data, error } = await authHelpers.enrollMfa();

      if (error) {
        toast.error(
          error.message || 'Failed to set up two-factor authentication'
        );
        return;
      }

      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setFactorId(data.id);
    } catch (error) {
      console.error('Enroll MFA error:', error);
      toast.error('Failed to set up two-factor authentication');
    } finally {
      setEnrolling(false);
    }
  };

  const handleVerifyAndActivate = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    try {
      setVerifying(true);
      const { error } = await authHelpers.challengeAndVerifyMfa({
        factorId,
        code: verificationCode
      });

      if (error) {
        toast.error(error.message || 'Invalid verification code');
        return;
      }

      toast.success('Two-factor authentication enabled successfully');
      setMfaEnabled(true);
      setQrCode(null);
      setSecret(null);
      setVerificationCode('');
    } catch (error) {
      console.error('Verify MFA error:', error);
      toast.error('Failed to verify code');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisableMfa = async () => {
    if (disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    try {
      setDisabling(true);

      // First verify MFA to reach AAL2
      const { error: verifyError } = await authHelpers.challengeAndVerifyMfa({
        factorId,
        code: disableCode
      });

      if (verifyError) {
        toast.error(verifyError.message || 'Invalid verification code');
        setDisabling(false);
        return;
      }

      // Now we're at AAL2, we can unenroll
      const { error } = await authHelpers.unenrollMfa(factorId);

      if (error) {
        toast.error(
          error.message || 'Failed to disable two-factor authentication'
        );
        return;
      }

      toast.success('Two-factor authentication disabled');
      setMfaEnabled(false);
      setFactorId(null);
      setShowDisableModal(false);
      setDisableCode('');
    } catch (error) {
      console.error('Disable MFA error:', error);
      toast.error('Failed to disable two-factor authentication');
    } finally {
      setDisabling(false);
    }
  };

  const handleCancelSetup = async () => {
    // Clean up the unverified factor
    if (factorId) {
      try {
        await authHelpers.unenrollMfa(factorId);
      } catch (error) {
        console.error('Error cleaning up factor:', error);
      }
    }
    setQrCode(null);
    setSecret(null);
    setVerificationCode('');
    setFactorId(null);
  };

  if (loading) {
    return (
      <Flex justifyContent="center" className="py-3">
        <Spinner animation="border" size="sm" />
      </Flex>
    );
  }

  // MFA is enabled
  if (mfaEnabled) {
    return (
      <>
        <Flex direction="column" gap={3}>
          <Flex alignItems="center" gap={2}>
            <span className="badge bg-success">Enabled</span>
            <span>Two-factor authentication is active on your account</span>
          </Flex>
          <p className="text-muted mb-0 fs--1">
            You will be asked to enter a code from your authenticator app when
            signing in.
          </p>
          <div>
            <Button
              variant="outline-danger"
              onClick={() => setShowDisableModal(true)}
            >
              Disable Two-Factor Authentication
            </Button>
          </div>
        </Flex>

        {/* Disable MFA Modal */}
        <Modal
          show={showDisableModal}
          onHide={() => {
            setShowDisableModal(false);
            setDisableCode('');
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Disable Two-Factor Authentication</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              To disable two-factor authentication, enter the 6-digit code from
              your authenticator app.
            </p>
            <Form.Group>
              <Form.Label>Verification code</Form.Label>
              <Form.Control
                type="text"
                placeholder="000000"
                maxLength={6}
                value={disableCode}
                onChange={e =>
                  setDisableCode(e.target.value.replace(/\D/g, ''))
                }
                autoComplete="one-time-code"
                inputMode="numeric"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDisableModal(false);
                setDisableCode('');
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="danger"
              onClick={handleDisableMfa}
              loading={disabling}
              disabled={disableCode.length !== 6}
            >
              Disable
            </LoadingButton>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  // Setup in progress - show QR code
  if (qrCode) {
    return (
      <Flex direction="column" gap={3}>
        <p className="mb-0">
          Scan this QR code with your authenticator app (Google Authenticator,
          Authy, etc.)
        </p>
        <Flex justifyContent="center">
          <img src={qrCode} alt="MFA QR Code" style={{ maxWidth: 200 }} />
        </Flex>
        <p className="text-muted fs--1 text-center mb-0">
          Or enter this code manually: <code>{secret}</code>
        </p>
        <Form.Group>
          <Form.Label>Enter the 6-digit code from your app</Form.Label>
          <Form.Control
            type="text"
            placeholder="000000"
            maxLength={6}
            value={verificationCode}
            onChange={e =>
              setVerificationCode(e.target.value.replace(/\D/g, ''))
            }
            style={{ maxWidth: 200 }}
          />
        </Form.Group>
        <Flex gap={2}>
          <LoadingButton
            onClick={handleVerifyAndActivate}
            loading={verifying}
            disabled={verificationCode.length !== 6}
          >
            Verify & Enable
          </LoadingButton>
          <Button variant="outline-secondary" onClick={handleCancelSetup}>
            Cancel
          </Button>
        </Flex>
      </Flex>
    );
  }

  // MFA not enabled - show enable button
  return (
    <Flex direction="column" gap={3}>
      <p className="mb-0">
        Add an extra layer of security to your account by requiring a
        verification code from an authenticator app when signing in.
      </p>
      <div>
        <LoadingButton onClick={handleEnrollMfa} loading={enrolling}>
          Set Up Two-Factor Authentication
        </LoadingButton>
      </div>
    </Flex>
  );
}
